/**
 * Guardrails management page (LDS).
 *
 * Displays kill-switch, dry-run toggles AND exception management
 * for each guardrail in a unified card view.
 *
 * Requirements: 2.6, 4.2, 4.5, 4.6, 4.8, 7.2, 8.1, 9.1, 10.1
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  LdsBox,
  LdsPaper,
  LdsTypography,
  LdsSwitch,
  LdsChip,
  LdsButton,
  LdsDialog,
  LdsAlert,
  LdsProgressCircular,
} from '@lds/react';
import { AxiosInstance } from 'axios';
import { ExceptionPanel, ExceptionType } from '../exceptions/ExceptionPanel';
import { GUARDRAIL_CATALOG } from '../guardrails/guardrailCatalog';
import styles from './TogglesPage.module.scss';

export type Role = 'admin' | 'viewer';

export interface GuardrailToggles {
  guardrail: string;
  unavailable?: boolean;
  killSwitch: {
    raw: string;
    active: boolean;
  };
  dryRun: {
    raw: string;
    mode: 'observe' | 'enforce';
  };
}

export interface TogglesPageProps {
  role: Role;
  apiClient: AxiosInstance;
  guardrailPanels?: Record<string, ReactNode>;
  /** Exception type definitions per guardrail (from config). */
  exceptionTypes?: Record<string, ExceptionType[]>;
}

export function TogglesPage({ role, apiClient, exceptionTypes = {}, guardrailPanels = {} }: TogglesPageProps) {
  const [toggles, setToggles] = useState<GuardrailToggles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    guardrail: string;
    toggle: 'kill-switch' | 'dry-run';
    newValue: string;
    description: string;
  }>({ open: false, guardrail: '', toggle: 'kill-switch', newValue: '', description: '' });

  const fetchToggles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<{ toggles: unknown[] }>('/toggles');
      const raw = response.data.toggles ?? [];
      // Backend returns snake_case keys (kill_switch, dry_run);
      // normalize to the camelCase interface expected by the UI.
      const normalized: GuardrailToggles[] = raw.map((item: any) => ({
        guardrail: item.guardrail,
        killSwitch: item.killSwitch ?? item.kill_switch ?? { raw: '', active: false },
        dryRun: item.dryRun ?? item.dry_run ?? { raw: '', mode: 'observe' as const },
      }));
      setToggles(normalized);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load toggles';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchToggles();
  }, [fetchToggles]);

  const handleToggleChange = (
    guardrail: string,
    toggle: 'kill-switch' | 'dry-run',
    newValue: string,
  ) => {
    // Sensitive actions that apply real remediation require confirmation.
    const isSensitive =
      (toggle === 'kill-switch' && newValue === 'false') || // activating guardrail
      (toggle === 'dry-run' && newValue === 'false'); // switching to enforce

    const description =
      toggle === 'kill-switch' && newValue === 'false'
        ? `Ativar o guardrail "${guardrail}" habilitará ações de remediação reais.`
        : toggle === 'dry-run' && newValue === 'false'
          ? `Habilitar enforce no guardrail "${guardrail}" aplicará remediação real.`
          : '';

    if (isSensitive) {
      setConfirmDialog({ open: true, guardrail, toggle, newValue, description });
    } else {
      executeToggleChange(guardrail, toggle, newValue);
    }
  };

  const executeToggleChange = async (
    guardrail: string,
    toggle: 'kill-switch' | 'dry-run',
    newValue: string,
  ) => {
    try {
      await apiClient.put(`/toggles/${guardrail}/${toggle}`, { value: newValue });
      await fetchToggles();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update toggle';
      setError(message);
    }
  };

  const handleConfirm = () => {
    const { guardrail, toggle, newValue } = confirmDialog;
    setConfirmDialog({ ...confirmDialog, open: false });
    executeToggleChange(guardrail, toggle, newValue);
  };

  const handleCancel = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleControlLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (!href.startsWith('/api/documents/')) return;

    event.preventDefault();
    setDocumentError(null);
    const documentWindow = window.open('about:blank', '_blank');
    if (!documentWindow) {
      setDocumentError('Permita a abertura de uma nova aba para acessar o documento.');
      return;
    }
    documentWindow.opener = null;

    void apiClient
      .get<Blob>(href.slice('/api'.length), { responseType: 'blob' })
      .then(({ data }) => {
        documentWindow.location.href = URL.createObjectURL(data);
      })
      .catch(() => {
        documentWindow.close();
        setDocumentError('Não foi possível abrir o documento PCI DSS. Tente novamente.');
      });
  };

  const visibleToggles: GuardrailToggles[] = [
    ...toggles,
    ...Object.keys(guardrailPanels).filter((guardrail) => !toggles.some((item) => item.guardrail === guardrail))
      .map((guardrail) => ({
        guardrail, unavailable: true,
        killSwitch: { raw: '', active: true },
        dryRun: { raw: '', mode: 'observe' as const },
      })),
  ];

  if (loading) {
    return (
      <LdsBox className={styles.centered} data-testid="toggles-loading">
        <LdsProgressCircular indeterminate animated ariaLabel="Carregando" />
      </LdsBox>
    );
  }

  if (error) {
    return (
      <LdsAlert color="critical" description={error} data-testid="toggles-error" />
    );
  }

  return (
    <LdsBox data-testid="toggles-page">
      <LdsTypography variant="headingXl">Guardrails</LdsTypography>
      {documentError && (
        <LdsAlert color="critical" description={documentError} />
      )}

      {visibleToggles.length === 0 && (
        <LdsBox className={styles.spacedTop}>
          <LdsAlert
            color="info"
            description="Nenhum guardrail configurado para este ambiente."
            data-testid="toggles-empty"
          />
        </LdsBox>
      )}

      <LdsBox className={styles.stack}>
        {visibleToggles.map((item) => {
          const meta = GUARDRAIL_CATALOG[item.guardrail];
          const guardrailActive = !item.killSwitch.active;

          return (
          <LdsPaper key={item.guardrail} elevated data-testid={`toggle-card-${item.guardrail}`}>
            <LdsBox className={styles.card}>
              <LdsBox className={styles.cardContent}>
                <LdsBox className={styles.cardDetails}>
              {/* Header — nome da guardrail */}
              <LdsTypography variant="headingMd">
                {meta?.displayName ?? item.guardrail}
              </LdsTypography>
              {meta && (
                <LdsTypography variant="bodySm" className={styles.guardrailId}>
                  {item.guardrail} - <span className={styles.guardrailCatalogId}>{meta.catalogId}</span>
                </LdsTypography>
              )}

              {/* Descrição */}
              {meta?.description && (
                <LdsBox className={styles.descriptionSection}>
                  <LdsTypography variant="headingXxs">Descrição</LdsTypography>
                  <LdsTypography variant="bodySm" className={styles.description}>
                    {meta.description}
                  </LdsTypography>
                  <LdsBox className={styles.controlsSection}>
                    <LdsTypography variant="headingXxs">Controles</LdsTypography>
                    <LdsBox className={styles.controlList}>
                      {meta.controls.map((control) => (
                        <LdsBox className={styles.controlRow} key={control.framework}>
                          <LdsTypography variant="bodyXs" className={styles.controlFramework}>
                            {control.framework}
                          </LdsTypography>
                          <LdsBox className={styles.controlIds}>
                            {control.controls.map(({ id, href }) => (
                              <a
                                className={styles.controlLink}
                                href={href}
                                onClick={(event) => handleControlLinkClick(event, href)}
                                key={`${control.framework}-${id}`}
                                rel="noreferrer"
                                target="_blank"
                              >
                                <LdsTypography variant="bodyXs">{id}</LdsTypography>
                              </a>
                            ))}
                          </LdsBox>
                        </LdsBox>
                      ))}
                    </LdsBox>
                  </LdsBox>
                </LdsBox>
              )}

                </LdsBox>
                <LdsBox className={styles.cardSettings}>
              {/* Estado do Guardrail (kill-switch invertido) */}
              <LdsBox className={styles.row}>
                <LdsTypography variant="headingXxs">Estado</LdsTypography>
                <LdsBox className={styles.controls}>
                  <LdsChip
                    label={item.unavailable ? 'Indispon\u00edvel' : guardrailActive ? 'Ativo' : 'Desativado'}
                    color={item.unavailable ? 'neutral' : guardrailActive ? 'success' : 'critical'}
                    size="sm"
                    data-testid={`killswitch-status-${item.guardrail}`}
                  />
                  {role === 'admin' && (
                    <LdsSwitch
                      checked={guardrailActive}
                      disabled={item.unavailable}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleToggleChange(
                          item.guardrail,
                          'kill-switch',
                          e.target.checked ? 'false' : 'true',
                        )
                      }
                      aria-label={`Toggle kill-switch for ${item.guardrail}`}
                      data-field={`killswitch-toggle-${item.guardrail}`}
                    />
                  )}
                </LdsBox>
              </LdsBox>

              {/* Modo de execução (dry-run) — desabilitado quando guardrail desativado */}
              <LdsBox className={`${styles.row} ${!guardrailActive ? styles.disabled : ''}`}>
                <LdsTypography variant="headingXxs">Modo de execução</LdsTypography>
                <LdsBox className={styles.controls}>
                  <LdsChip
                    label={item.unavailable ? 'Indispon\u00edvel' : item.dryRun.mode === 'observe' ? 'Observe' : 'Enforce'}
                    color={item.unavailable ? 'neutral' : item.dryRun.mode === 'observe' ? 'warning' : 'success'}
                    size="sm"
                    data-testid={`dryrun-status-${item.guardrail}`}
                  />
                  {role === 'admin' && (
                    <LdsSwitch
                      checked={item.dryRun.mode === 'enforce'}
                      disabled={item.unavailable || !guardrailActive}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleToggleChange(
                          item.guardrail,
                          'dry-run',
                          e.target.checked ? 'false' : 'true',
                        )
                      }
                      aria-label={`Toggle dry-run for ${item.guardrail}`}
                      data-field={`dryrun-toggle-${item.guardrail}`}
                    />
                  )}
                </LdsBox>
              </LdsBox>
                </LdsBox>
              </LdsBox>

              {/* Exceptions Panel */}
              {guardrailPanels[item.guardrail] ?? <ExceptionPanel
                guardrail={item.guardrail}
                role={role}
                apiClient={apiClient}
                exceptionTypes={exceptionTypes[item.guardrail] ?? [{ key: 'access_key_id', label: 'Access Key ID', pattern: 'exact', placeholder: 'AKIA...' }]}
              />}
            </LdsBox>
          </LdsPaper>
          );
        })}
      </LdsBox>

      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <LdsDialog
          open={confirmDialog.open}
          onClose={handleCancel}
          title="Confirmar ação"
          closeButtonAriaLabel="Fechar"
          data-testid="confirm-dialog"
          footer={
            <>
              <LdsButton variant="outlined" onClick={handleCancel} data-action="confirm-cancel">
                Cancelar
              </LdsButton>
              <LdsButton
                variant="contained"
                color="critical"
                onClick={handleConfirm}
                data-action="confirm-proceed"
              >
                Confirmar
              </LdsButton>
            </>
          }
        >
          <LdsTypography variant="bodyMd">{confirmDialog.description}</LdsTypography>
          <LdsTypography variant="bodyMd">Tem certeza de que deseja prosseguir?</LdsTypography>
        </LdsDialog>
      )}
    </LdsBox>
  );
}
